/**
 * Вкладка «Пакеты» каталога программ обучения (training-packages-approval; ТЗ 26.10.2026, п.4.4.2
 * «Добавить пакет программ: название пакета – выбор программ для пакета – цена»; ответы
 * заказчицы 27.10, В9 — пакет выбирается целиком, с одним списком слушателей, цену клиент не
 * видит). Пакеты показываются клиенту первой веткой «Популярные пакеты программ» в порядке этого
 * списка.
 *
 * Добавить пакет может любой сотрудник команды — у администратора он действует сразу, у
 * остальных ждёт подтверждения (п.4.4.3). Подтверждает, отклоняет, правит, меняет порядок и
 * убирает в архив только администратор; автор может удалить своё неподтверждённое.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  Check,
  Package,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import {
  adminApi,
  teamApi,
  trainingApi,
  type CatalogStatus,
  type Program,
  type ProgramPackage,
  type ProgramPackageInput,
  type TrainingDirectionItem,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { directionPathLabel, flattenDirections, formatRubles, programLine } from '@/lib/training-catalog'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import {
  CatalogStatusBadge,
  ProgramPickerDialog,
  ProposalMeta,
  RejectDialog,
  useCatalogConfirm,
} from './catalog-shared'

export type PackagesShow = 'active' | 'pending' | 'rejected' | 'archived'

const SHOW_LABELS: Record<PackagesShow, string> = {
  active: 'Действующие',
  pending: 'На подтверждении',
  rejected: 'Отклонённые',
  archived: 'Архив',
}

function packageShow(p: { status: CatalogStatus; archived: boolean }): PackagesShow {
  if (p.status !== 'active') return p.status
  return p.archived ? 'archived' : 'active'
}

/** Минимум программ в пакете (одна программа — это не пакет). */
const MIN_PROGRAMS = 2

interface PackageDraft {
  name: string
  /** Программы в порядке пакета. */
  programs: { id: string; label: string; code: string }[]
  /** Цена в рублях (целое). */
  price: string
}

function emptyDraft(): PackageDraft {
  return { name: '', programs: [], price: '' }
}

function draftFromPackage(p: ProgramPackage): PackageDraft {
  return {
    name: p.name,
    programs: p.items.map((it) => ({ id: it.programId, label: programLine(it), code: it.code })),
    price: p.priceKopecks == null ? '' : String(Math.round(p.priceKopecks / 100)),
  }
}

function draftToInput(d: PackageDraft): ProgramPackageInput {
  return {
    name: d.name.trim(),
    programIds: d.programs.map((p) => p.id),
    priceKopecks: d.price.trim() ? Number(d.price) * 100 : null,
  }
}

function PackageForm({
  draft,
  setDraft,
  programs,
  sectionOf,
  busy,
  submitLabel,
  note,
  onSubmit,
  onCancel,
}: {
  draft: PackageDraft
  setDraft: (d: PackageDraft) => void
  /** Действующие программы каталога — из них собирается пакет. */
  programs: Program[]
  sectionOf: (p: Program) => string
  busy: boolean
  submitLabel: string
  note?: string
  onSubmit: () => void
  onCancel: () => void
}) {
  const [picking, setPicking] = useState(false)
  const chosen = new Set(draft.programs.map((p) => p.id))
  const priceInvalid =
    draft.price.trim() !== '' && !(Number.isInteger(Number(draft.price)) && Number(draft.price) >= 0)
  const tooFew = draft.programs.length < MIN_PROGRAMS
  const canSubmit = draft.name.trim().length > 0 && !tooFew && !priceInvalid

  function submit(e: FormEvent) {
    e.preventDefault()
    if (canSubmit && !busy) onSubmit()
  }

  return (
    <form onSubmit={submit} noValidate>
      {note ? <p className="mb-3 text-14 text-txt-secondary">{note}</p> : null}
      <div className="grid grid-cols-1 gap-3 nw:grid-cols-3">
        <Field label="Название пакета" required className="my-0 nw:col-span-2">
          {(props) => (
            <Input
              {...props}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="напр. Охрана труда 2: Б + Первая помощь"
            />
          )}
        </Field>
      </div>
      <div className="mt-3">
        <p className="mb-1.5 text-13 font-medium text-txt">
          Программы пакета <span className="font-normal text-txt-secondary">— не меньше двух</span>
        </p>
        {draft.programs.length > 0 ? (
          <ul className="mb-2 divide-y divide-line rounded-8 border border-line" aria-label="Программы пакета">
            {draft.programs.map((p) => (
              <li key={p.id} className="flex items-center gap-2 px-3 py-2">
                <span className="min-w-0 flex-1 break-words text-14 text-txt">
                  <span className="mr-2 text-txt-secondary">{p.code}</span>
                  {p.label}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  aria-label={`Убрать из пакета «${p.label}»`}
                  title="Убрать из пакета"
                  onClick={() => setDraft({ ...draft, programs: draft.programs.filter((x) => x.id !== p.id) })}
                  leftIcon={<X className="size-4 stroke-current" aria-hidden />}
                />
              </li>
            ))}
          </ul>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => setPicking(true)}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить программу
        </Button>
      </div>
      {/* Порядок полей — как у заказчицы (ТЗ 26.10.2026, п.4.4.2): название — программы — цена. */}
      <div className="mt-3 grid grid-cols-1 gap-3 nw:grid-cols-3">
        <Field
          label="Цена, ₽"
          hint="Видна только сотрудникам Центрики"
          error={priceInvalid ? 'Целое число рублей' : undefined}
          className="my-0"
        >
          {(props) => (
            <Input
              {...props}
              inputMode="numeric"
              invalid={priceInvalid}
              value={draft.price}
              onChange={(e) => setDraft({ ...draft, price: e.target.value })}
            />
          )}
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={!canSubmit || busy}>
          {busy ? 'Сохранение…' : submitLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={onCancel}
          leftIcon={<X className="size-4 stroke-current" aria-hidden />}
        >
          Отмена
        </Button>
      </div>
      {picking ? (
        <ProgramPickerDialog
          title="Программа в пакет"
          programs={programs.filter((p) => !chosen.has(p.id))}
          sectionOf={sectionOf}
          onClose={() => setPicking(false)}
          onPick={(p) => {
            setDraft({ ...draft, programs: [...draft.programs, { id: p.id, label: programLine(p), code: p.code }] })
            setPicking(false)
          }}
        />
      ) : null}
    </form>
  )
}

export function PackagesTab({
  isAdmin,
  accountId,
  initialShow = 'active',
}: {
  isAdmin: boolean
  accountId: string
  initialShow?: PackagesShow
}) {
  const [packages, setPackages] = useState<ProgramPackage[]>([])
  const [programs, setPrograms] = useState<Program[]>([])
  const [directions, setDirections] = useState<TrainingDirectionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [show, setShow] = useState<PackagesShow>(initialShow)
  // Переход из «колокольчика» при уже открытой вкладке: фильтр — из адреса.
  useEffect(() => setShow(initialShow), [initialShow])

  const [createOpen, setCreateOpen] = useState(false)
  const [createDraft, setCreateDraft] = useState<PackageDraft>(emptyDraft)
  const [editId, setEditId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState<PackageDraft>(emptyDraft)
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<ProgramPackage | null>(null)
  // «В архив» и «Удалить» — с подтверждением (training-review-3).
  const [confirmDialog, confirmAction] = useCatalogConfirm()

  const nodes = useMemo(() => flattenDirections(directions), [directions])
  const selectable = useMemo(() => programs.filter((p) => !p.archived && p.status === 'active'), [programs])
  const sectionOf = (p: Program) => directionPathLabel(nodes, p.directionId) || p.directionName
  const counts = useMemo(() => {
    const out: Record<PackagesShow, number> = { active: 0, pending: 0, rejected: 0, archived: 0 }
    for (const p of packages) out[packageShow(p)]++
    return out
  }, [packages])
  const shown = useMemo(() => packages.filter((p) => packageShow(p) === show), [packages, show])

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([
      trainingApi.listPackages(controller.signal),
      trainingApi.listPrograms(controller.signal),
      trainingApi.listDirections(controller.signal),
    ])
      .then(([pk, p, d]) => {
        if (controller.signal.aborted) return
        setPackages(pk)
        setPrograms(p)
        setDirections(d)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить пакеты'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  function replace(updated: ProgramPackage) {
    setPackages((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
  }

  async function run(id: string | null, fallback: string, action: () => Promise<void>) {
    setActionError(null)
    setNotice(null)
    setBusyId(id)
    try {
      await action()
    } catch (err) {
      setActionError(getErrorMessage(err, fallback))
    } finally {
      setBusyId(null)
    }
  }

  async function create() {
    setSaving(true)
    await run(null, 'Не удалось добавить пакет', async () => {
      const created = await teamApi.training.createPackage(draftToInput(createDraft))
      setPackages((prev) => [...prev, created])
      setCreateDraft(emptyDraft())
      setCreateOpen(false)
      setShow(packageShow(created))
      if (created.status === 'pending') {
        setNotice(`Пакет «${created.name}» отправлен на подтверждение. Клиенты увидят его после подтверждения администратором.`)
      }
    })
    setSaving(false)
  }

  async function saveEdit() {
    if (!editId) return
    setSaving(true)
    await run(editId, 'Не удалось сохранить пакет', async () => {
      replace(await adminApi.training.updatePackage(editId, draftToInput(editDraft)))
      setEditId(null)
    })
    setSaving(false)
  }

  const review = (p: ProgramPackage, decision: 'approve' | 'reject', reason = '') =>
    run(p.id, decision === 'approve' ? 'Не удалось подтвердить пакет' : 'Не удалось отклонить пакет', async () => {
      const updated = await adminApi.training.reviewPackage(p.id, decision === 'approve' ? { decision } : { decision, reason })
      replace(updated)
      setRejecting(null)
      setNotice(
        decision === 'approve'
          ? `Пакет «${updated.name}» подтверждён и действует в каталоге.`
          : `Пакет «${updated.name}» отклонён, автор получит уведомление.`,
      )
    })

  const archive = (p: ProgramPackage, archived: boolean) =>
    run(p.id, archived ? 'Не удалось убрать пакет в архив' : 'Не удалось вернуть пакет', async () => {
      replace(await adminApi.training.archivePackage(p.id, archived))
    })

  const move = (p: ProgramPackage, up: boolean) =>
    run(p.id, 'Не удалось изменить порядок', async () => {
      setPackages(await adminApi.training.movePackage(p.id, up))
    })

  const remove = (p: ProgramPackage) =>
    run(p.id, 'Не удалось удалить пакет', async () => {
      await teamApi.training.deletePackage(p.id)
      setPackages((prev) => prev.filter((x) => x.id !== p.id))
    })

  return (
    <div>
      <p className="mb-4 text-14 text-txt-secondary">
        Пакет клиент выбирает целиком — на все программы пакета один список слушателей. Цену клиент не видит. Пакеты
        показываются первыми, в порядке этого списка.
      </p>
      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}
      {notice ? (
        <Alert variant="info" className="mb-4">
          {notice}
        </Alert>
      ) : null}
      {isAdmin && counts.pending > 0 && show !== 'pending' ? (
        <Alert variant="info" className="mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <span>На подтверждении пакетов: {counts.pending}</span>
            <Button size="sm" variant="secondary" onClick={() => setShow('pending')}>
              Показать
            </Button>
          </div>
        </Alert>
      ) : null}

      {createOpen ? (
        <Card header="Новый пакет" className="mb-4">
          <PackageForm
            draft={createDraft}
            setDraft={setCreateDraft}
            programs={selectable}
            sectionOf={sectionOf}
            busy={saving}
            note={isAdmin ? undefined : 'Пакет появится в каталоге после подтверждения администратором.'}
            submitLabel={isAdmin ? 'Добавить пакет' : 'Отправить на подтверждение'}
            onSubmit={() => void create()}
            onCancel={() => setCreateOpen(false)}
          />
        </Card>
      ) : (
        <div className="mb-4">
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            disabled={loading || Boolean(error)}
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
          >
            Новый пакет
          </Button>
        </div>
      )}

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : (
        <Panel>
          <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
            <Field label="Показать" className="my-0 w-full nw:w-60">
              {(props) => (
                <Select value={show} onValueChange={(v) => setShow(v as PackagesShow)}>
                  <SelectTrigger id={props.id} aria-label="Показать пакеты">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(SHOW_LABELS) as PackagesShow[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {SHOW_LABELS[k]}
                        {k === 'active' ? '' : ` (${counts[k]})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <p className="ml-auto text-13 text-txt-secondary">Действующих пакетов: {counts.active}</p>
          </div>
          {shown.length === 0 ? (
            <EmptyState
              icon={Package}
              title={show === 'active' ? 'Пакетов пока нет' : 'Здесь пусто'}
              description={
                show === 'active'
                  ? 'Добавьте пакет: название, программы и цену'
                  : `Пакетов «${SHOW_LABELS[show].toLowerCase()}» нет`
              }
            />
          ) : (
            <ul>
              {shown.map((p, i) =>
                editId === p.id ? (
                  <li key={p.id} className="border-b border-line px-4 py-3 last:border-b-0">
                    <PackageForm
                      draft={editDraft}
                      setDraft={setEditDraft}
                      programs={selectable}
                      sectionOf={sectionOf}
                      busy={saving}
                      submitLabel="Сохранить"
                      onSubmit={() => void saveEdit()}
                      onCancel={() => setEditId(null)}
                    />
                  </li>
                ) : (
                  <li
                    key={p.id}
                    className="flex flex-wrap items-start gap-3 border-b border-line px-4 py-3 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="break-words font-medium text-txt">
                        {p.name}
                        <CatalogStatusBadge status={p.status} archived={p.archived} />
                        {p.status === 'active' && !p.archived && !p.available ? (
                          <Badge status="warning" className="ml-2 align-middle">
                            Недоступен клиентам
                          </Badge>
                        ) : null}
                      </div>
                      <ul className="mt-1 text-13 text-txt-secondary" aria-label={`Программы пакета «${p.name}»`}>
                        {p.items.map((it) => (
                          <li key={it.programId} className="break-words">
                            <span className="mr-2 text-txt-tertiary">{it.code}</span>
                            {programLine(it)}
                            {it.archived ? <span className="text-red"> — в архиве, замените</span> : null}
                            {!it.archived && it.status !== 'active' ? (
                              <span className="text-red"> — не подтверждена</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                      <ProposalMeta
                        status={p.status}
                        createdByName={p.createdByName}
                        createdAt={p.createdAt}
                        reviewNote={p.reviewNote}
                      />
                    </div>
                    <div className="whitespace-nowrap pt-0.5 text-14 text-txt">
                      {formatRubles(p.priceKopecks) || <span className="text-txt-tertiary">Цена не задана</span>}
                    </div>
                    <div className="flex gap-1">
                      <PackageActions
                        p={p}
                        isAdmin={isAdmin}
                        own={p.createdById === accountId}
                        busy={busyId === p.id}
                        first={i === 0}
                        last={i === shown.length - 1}
                        onMove={(up) => void move(p, up)}
                        onApprove={() => void review(p, 'approve')}
                        onReject={() => setRejecting(p)}
                        onEdit={() => {
                          setEditId(p.id)
                          setEditDraft(draftFromPackage(p))
                        }}
                        onArchive={(archived) =>
                          archived
                            ? confirmAction({
                                title: `Убрать пакет «${p.name}» в архив?`,
                                message:
                                  'Пакет пропадёт из выбора клиентов. Сданные заказы с ним не изменятся.',
                                confirmLabel: 'В архив',
                                onConfirm: () => void archive(p, true),
                              })
                            : void archive(p, false)
                        }
                        onRemove={() =>
                          confirmAction({
                            title: `Удалить пакет «${p.name}»?`,
                            message: 'Пакет будет удалён из каталога насовсем.',
                            confirmLabel: 'Удалить',
                            danger: true,
                            onConfirm: () => void remove(p),
                          })
                        }
                      />
                    </div>
                  </li>
                ),
              )}
            </ul>
          )}
        </Panel>
      )}
      {confirmDialog}
      <RejectDialog
        open={rejecting !== null}
        title={rejecting ? `Отклонить пакет «${rejecting.name}»?` : ''}
        busy={rejecting !== null && busyId === rejecting.id}
        onConfirm={(reason) => rejecting && void review(rejecting, 'reject', reason)}
        onClose={() => setRejecting(null)}
      />
    </div>
  )
}

/** Действия пакета иконками с подписью; набор — как у программы (см. ProgramActions). */
function PackageActions({
  p,
  isAdmin,
  own,
  busy,
  first,
  last,
  onMove,
  onApprove,
  onReject,
  onEdit,
  onArchive,
  onRemove,
}: {
  p: ProgramPackage
  isAdmin: boolean
  own: boolean
  busy: boolean
  first: boolean
  last: boolean
  onMove: (up: boolean) => void
  onApprove: () => void
  onReject: () => void
  onEdit: () => void
  onArchive: (archived: boolean) => void
  onRemove: () => void
}) {
  const icon = (Icon: typeof Pencil) => <Icon className="size-4 stroke-current" aria-hidden />
  const removeButton = (
    <Button
      size="sm"
      variant="secondary"
      aria-label={`Удалить пакет «${p.name}»`}
      title="Удалить"
      disabled={busy}
      onClick={onRemove}
      leftIcon={icon(Trash2)}
    />
  )
  if (!isAdmin) return own && p.status !== 'active' ? removeButton : null
  const ordered = p.status === 'active' && !p.archived
  return (
    <>
      {ordered ? (
        <>
          <Button
            size="sm"
            variant="secondary"
            aria-label={`Пакет «${p.name}» выше`}
            title="Выше"
            disabled={busy || first}
            onClick={() => onMove(true)}
            leftIcon={icon(ArrowUp)}
          />
          <Button
            size="sm"
            variant="secondary"
            aria-label={`Пакет «${p.name}» ниже`}
            title="Ниже"
            disabled={busy || last}
            onClick={() => onMove(false)}
            leftIcon={icon(ArrowDown)}
          />
        </>
      ) : null}
      {p.status !== 'active' ? (
        <Button
          size="sm"
          variant="secondary"
          aria-label={`Подтвердить пакет «${p.name}»`}
          title="Подтвердить"
          disabled={busy}
          onClick={onApprove}
          leftIcon={icon(Check)}
        />
      ) : null}
      {p.status === 'pending' ? (
        <Button
          size="sm"
          variant="secondary"
          aria-label={`Отклонить пакет «${p.name}»`}
          title="Отклонить"
          disabled={busy}
          onClick={onReject}
          leftIcon={icon(X)}
        />
      ) : null}
      <Button size="sm" variant="secondary" aria-label="Править" title="Править" onClick={onEdit} leftIcon={icon(Pencil)} />
      {p.status === 'active' ? (
        <Button
          size="sm"
          variant="secondary"
          aria-label={p.archived ? 'Вернуть' : 'В архив'}
          title={p.archived ? 'Вернуть из архива' : 'Убрать в архив'}
          disabled={busy}
          onClick={() => onArchive(!p.archived)}
          leftIcon={p.archived ? icon(ArchiveRestore) : icon(Archive)}
        />
      ) : null}
      {removeButton}
    </>
  )
}
